import React, { useState, useEffect } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Slide,
  MenuItem,
  IconButton,
  Typography,
} from "@mui/material";
import { AddCircle, Delete } from "@mui/icons-material";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import { useAuth } from "../../../contexts/AuthContext";
import "./GuestModal.css";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const API = process.env.REACT_APP_API_BASE;

const GuestModal = ({
  open,
  onClose,
  initialData,
  onSuccess,
  weddingId,
  hasUnlimitedGuestsProduct = false,
  currentTotalGuests = 0,
  guestLimit = 99,
}) => {
  const { paymentsEnforced } = useAuth();
  const [mainGuest, setMainGuest] = useState({
    name: "",
    last_name: "",
    relationship_category: "FAMILY",
    age_category: "ADULT",
    RSVP_status: "CONFIRMED",
    is_main_guest: true,
    seated: false,
    side: "BRIDE",
    wedding_id: weddingId,
  });

  const [otherGuests, setOtherGuests] = useState([]);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (open) {
      setSubmitError("");
      if (initialData) {
        setMainGuest(initialData.mainGuest);
        setOtherGuests(initialData.sideGuests || []);
      } else {
        setMainGuest({
          name: "",
          last_name: "",
          relationship_category: "FAMILY",
          age_category: "ADULT",
          RSVP_status: "CONFIRMED",
          is_main_guest: true,
          seated: false,
          side: "BRIDE",
          wedding_id: weddingId,
        });
        setOtherGuests([]);
      }
    }
  }, [open, initialData, weddingId]);

  const handleMainChange = (e) => {
    const { name, value } = e.target;
    setMainGuest((prev) => ({ ...prev, [name]: value }));
  };

  const addGuest = () => {
    setOtherGuests((prev) => [
      ...prev,
      {
        name: "",
        last_name: "",
        relationship_category: "FAMILY",
        age_category: "ADULT",
        RSVP_status: "CONFIRMED",
        is_main_guest: false,
        seated: false,
      },
    ]);
  };

  const updateOtherGuest = (index, field, value) => {
    setOtherGuests((prev) =>
      prev.map((g, i) => (i === index ? { ...g, [field]: value } : g))
    );
  };

  const removeOtherGuest = (index) => {
    setOtherGuests((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      setSubmitError("");
      let guests = [];

      if (initialData) {
        guests = [mainGuest, ...otherGuests];
      } else {
        guests = [
          mainGuest,
          ...otherGuests.map((g) => ({
            ...g,
            side: mainGuest.side,
            wedding_id: mainGuest.wedding_id,
            seated: false,
          })),
        ];
      }

      const url = initialData ? `${API}/guest-group` : `${API}/guests/group`;
      const method = initialData ? "PUT" : "POST";

      if (!initialData && !hasUnlimitedGuestsProduct) {
        const nextTotal = Number(currentTotalGuests) + guests.length;
        if (nextTotal > guestLimit) {
          setSubmitError(
            `Лимитот без upgrade е ${guestLimit} гости. Моментално имате ${currentTotalGuests}.`
          );
          return;
        }
      }

      const response = await fetchWithAuth(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(guests),
      });

      if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || "Failed to save guest group");
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving guests:", error);
      const message = String(error?.message || "");
      if (
        paymentsEnforced &&
        (message.includes("403") || message.toLowerCase().includes("forbidden"))
      ) {
        setSubmitError(
          `Не можете да додадете повеќе гости без upgrade. Лимитот е ${guestLimit}.`
        );
        return;
      }
      setSubmitError("Настана грешка при зачувување на гости.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted
      className="guest-modal"
      PaperProps={{ className: "custom-dialog-paper" }}
    >
      <DialogTitle className="modal-title">
        {initialData ? "Измени група гости" : "Додади група гости"}
      </DialogTitle>

      <DialogContent dividers className="modal-content">
        {submitError ? <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert> : null}
        {/* Main guest */}
        <Typography variant="h6" gutterBottom>
          Главен гостин
        </Typography>

        <Box
          mb={2}
          p={2}
          border="1px solid #ccc"
          borderRadius={2}
          className="guest-frame"
        >
          <Box display="flex" alignItems="center" mb={1}>
            <Typography variant="h6">Гостин 1</Typography>
          </Box>

          <Box display="flex" gap={2} mb={2}>
            <TextField
              name="name"
              label="Име"
              value={mainGuest.name}
              onChange={handleMainChange}
              fullWidth
              className="modal-input"
            />
            <TextField
              name="last_name"
              label="Презиме"
              value={mainGuest.last_name}
              onChange={handleMainChange}
              fullWidth
              className="modal-input"
            />
          </Box>

          <Box display="flex" gap={2} mb={2}>
            <TextField
              name="relationship_category"
              label="Однос"
              select
              value={mainGuest.relationship_category}
              onChange={handleMainChange}
              fullWidth
              className="modal-input"
            >
              <MenuItem value="FAMILY">Семејство</MenuItem>
              <MenuItem value="FRIEND">Пријател</MenuItem>
              <MenuItem value="COWORKER">Колега</MenuItem>
              <MenuItem value="OTHER">Друго</MenuItem>
            </TextField>
            <TextField
              name="age_category"
              label="Возраст"
              select
              value={mainGuest.age_category}
              onChange={handleMainChange}
              fullWidth
              className="modal-input"
            >
              <MenuItem value="ADULT">Возрасен</MenuItem>
              <MenuItem value="CHILD">Дете</MenuItem>
              <MenuItem value="BABY">Бебе</MenuItem>
            </TextField>
          </Box>

          <Box display="flex" gap={2} mb={2}>
            <TextField
              name="side"
              label="Страна"
              select
              value={mainGuest.side}
              onChange={handleMainChange}
              fullWidth
              className="modal-input"
            >
              <MenuItem value="BRIDE">Невеста</MenuItem>
              <MenuItem value="GROOM">Младоженец</MenuItem>
              <MenuItem value="BOTH">Двете страни</MenuItem>
            </TextField>
            <TextField
              name="RSVP_status"
              label="RSVP Статус"
              select
              value={mainGuest.RSVP_status}
              onChange={handleMainChange}
              fullWidth
              className="modal-input"
            >
              <MenuItem value="CONFIRMED">Потврдено присуство</MenuItem>
              <MenuItem value="UNKNOWN">Неодговорена покана</MenuItem>
              <MenuItem value="REJECTED">Одбиено присуство</MenuItem>
            </TextField>
          </Box>
        </Box>

        {/* Side guests */}
        {(otherGuests.length > 0 || !initialData) && (
          <>
            <Typography variant="h6" gutterBottom>
              Дополнителни гости
            </Typography>
            {otherGuests.map((guest, idx) => (
              <Box
                key={idx}
                mb={2}
                p={2}
                border="1px solid #ccc"
                borderRadius={2}
                className="guest-frame"
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography variant="subtitle1">Гостин {idx + 2}</Typography>
                  {!initialData && (
                    <IconButton
                      className="guest-delete"
                      onClick={() => removeOtherGuest(idx)}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Box>

                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                  <TextField
                    label="Име"
                    value={guest.name}
                    onChange={(e) =>
                      updateOtherGuest(idx, "name", e.target.value)
                    }
                    fullWidth
                    className="modal-input"
                  />
                  <TextField
                    label="Презиме"
                    value={guest.last_name}
                    onChange={(e) =>
                      updateOtherGuest(idx, "last_name", e.target.value)
                    }
                    fullWidth
                    className="modal-input"
                  />
                  <TextField
                    label="Однос"
                    select
                    value={guest.relationship_category}
                    onChange={(e) =>
                      updateOtherGuest(
                        idx,
                        "relationship_category",
                        e.target.value
                      )
                    }
                    fullWidth
                    className="modal-input"
                  >
                    <MenuItem value="FAMILY">Семејство</MenuItem>
                    <MenuItem value="FRIEND">Пријател</MenuItem>
                    <MenuItem value="COWORKER">Колега</MenuItem>
                    <MenuItem value="OTHER">Друго</MenuItem>
                  </TextField>
                  <TextField
                    label="Возраст"
                    select
                    value={guest.age_category}
                    onChange={(e) =>
                      updateOtherGuest(idx, "age_category", e.target.value)
                    }
                    fullWidth
                    className="modal-input"
                  >
                    <MenuItem value="ADULT">Возрасен</MenuItem>
                    <MenuItem value="CHILD">Дете</MenuItem>
                    <MenuItem value="BABY">Бебе</MenuItem>
                  </TextField>
                </Box>
              </Box>
            ))}

            {!initialData && (
              <Button
                startIcon={<AddCircle />}
                onClick={addGuest}
                variant="outlined"
                sx={{ mt: 1 }}
                className="guest-add"
              >
                Додади гостин
              </Button>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions className="guest-actions">
        <Button onClick={onClose} variant="outlined" className="guest-cancel">
          Откажи
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          className="guest-submit"
        >
          {initialData ? "Зачувај измени" : "Додади група"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GuestModal;
